/**
 * Controls Catalog Management — list, create, edit, change status.
 * Matches Actor Catalog styling and interaction patterns.
 */
import { controlCatalogService } from '../services/api';
import { sanitize, CTRL_CATEGORIES } from '../services/utils';

let _container: HTMLElement;
let _controls: any[] = [];
let _showHidden = false;
let _filter = '';

export async function renderControlCatalog(container: HTMLElement): Promise<void> {
  _container = container;
  await loadControls();
  render();
}

async function loadControls(): Promise<void> {
  const { controls } = await controlCatalogService.list(_showHidden);
  _controls = controls;
}

const toggleBtnStyle = (active: boolean) =>
  `padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:500;cursor:pointer;border:1px solid ${active ? 'var(--colorAccent)' : 'var(--colorBorder)'};background:${active ? 'var(--colorAccent)' : 'var(--colorBgCard)'};color:${active ? '#fff' : 'var(--colorTextMuted)'};transition:all .15s`;

const labelStyle = 'font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--colorTextLight);margin-bottom:6px';
const chipBase = 'display:inline-flex;align-items:center;padding:5px 14px;border-radius:9999px;font-size:13px;cursor:pointer;border:1px solid var(--colorBorder);transition:all .15s;user-select:none';
const chipActive = 'background:var(--colorAccent);color:#fff;border-color:var(--colorAccent)';
const chipInactive = 'background:var(--colorBgCard);color:var(--colorTextMuted)';

function render(): void {
  const q = _filter.toLowerCase();
  const filtered = q ? _controls.filter((c) =>
    c.label.toLowerCase().includes(q) || (c.control_id || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q)
  ) : _controls;

  const grouped: Record<string, any[]> = {};
  filtered.forEach((c) => {
    if (!grouped[c.category]) grouped[c.category] = [];
    grouped[c.category].push(c);
  });

  // Populate toolbar in the header row
  const toolbar = document.getElementById('admin-sub-toolbar');
  if (toolbar) {
    toolbar.innerHTML = `
      <input id="control-filter" type="text" placeholder="Filter..." value="${sanitize(_filter)}"
        style="padding:4px 12px;border:1px solid var(--colorBorderInput);border-radius:9999px;font-size:11px;background:var(--colorBgInput);color:var(--colorText);width:160px;box-sizing:border-box">
      <button id="toggle-hidden-controls" style="${toggleBtnStyle(_showHidden)}">
        ${_showHidden ? 'Showing Hidden' : 'Show Hidden'}
      </button>
      <button id="add-control-btn" style="padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:500;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">+ Add Control</button>
    `;
  }

  _container.innerHTML = `
    ${Object.keys(grouped).length === 0
      ? `<div style="text-align:center;padding:32px 0;color:var(--colorTextLight);font-size:13px">${q ? 'No controls match your filter.' : 'No controls found.'}</div>`
      : Object.entries(grouped).map(([cat, controls]) => {
        const catDef = CTRL_CATEGORIES[cat];
        return `
          <div style="margin-bottom:20px">
            <div style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;margin-bottom:8px;display:flex;align-items:center;gap:6px">
              <span style="width:8px;height:8px;border-radius:50%;background:${catDef?.color || 'var(--colorTextLight)'}"></span>
              <span style="color:var(--colorTextLight)">${catDef?.label || cat} (${controls.length})</span>
            </div>
            <div style="display:grid;gap:4px">
              ${controls.map((c) => renderControlRow(c)).join('')}
            </div>
          </div>
        `;
      }).join('')}
  `;

  document.getElementById('toggle-hidden-controls')?.addEventListener('click', async () => {
    _showHidden = !_showHidden;
    await loadControls();
    render();
  });

  document.getElementById('control-filter')?.addEventListener('input', (e) => {
    _filter = (e.target as HTMLInputElement).value;
    render();
    const input = document.getElementById('control-filter') as HTMLInputElement;
    if (input) { input.focus(); input.selectionStart = input.selectionEnd = input.value.length; }
  });

  document.getElementById('add-control-btn')?.addEventListener('click', showCreateControlModal);

  // Clickable rows open edit modal
  document.querySelectorAll('[data-edit-control]').forEach((el) => {
    el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.editControl!;
      const control = _controls.find((c) => c._id === id);
      if (control) showEditControlModal(control);
    });
  });
}

function renderControlRow(c: any): string {
  const statusColors: Record<string, string> = { active: 'var(--colorSuccess)', hidden: 'var(--colorWarning)', deleted: 'var(--colorError)' };
  const catDef = CTRL_CATEGORIES[c.category];
  return `
    <div data-edit-control="${c._id}" style="display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:6px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);cursor:pointer;transition:border-color .15s;${c.status !== 'active' ? 'opacity:.6;' : ''}" onmouseover="this.style.borderColor='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)'">
      <span style="width:8px;height:8px;border-radius:50%;background:${catDef?.color || 'var(--colorTextLight)'};flex-shrink:0"></span>
      <div style="flex:1;min-width:0">
        <div style="font-size:12px;font-weight:500;color:var(--colorTextHeading)">${sanitize(c.label)}</div>
        <div style="font-size:10px;color:var(--colorTextMuted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${sanitize(c.description)}</div>
      </div>
      <span style="font-size:9px;padding:2px 6px;border-radius:4px;color:${statusColors[c.status] || 'var(--colorTextLight)'};border:1px solid ${statusColors[c.status] || 'var(--colorBorder)'}">${c.status}</span>
    </div>
  `;
}

// ─── Modal ─────────────────────────────────────────────

function controlFormModal(title: string, data: any, onSubmit: () => Promise<void>): void {
  const currentStatus = data.status || 'active';

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  const modal = document.createElement('div');
  modal.className = 'modal-box';
  modal.style.maxWidth = '600px';
  modal.innerHTML = `
    <form id="control-modal-form">
      <!-- Control preview layout -->
      <div style="display:flex;align-items:flex-start;gap:14px;padding:16px;border-radius:8px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);margin-bottom:20px">
        <div style="flex:1;min-width:0">
          ${data.control_id
            ? `<div style="font-size:9px;color:var(--colorTextLight);font-family:monospace;margin-bottom:4px">${sanitize(data.control_id)}</div>`
            : `<div style="margin-bottom:6px"><input id="cf-id" value="" placeholder="control-id" required style="font-size:10px;border:1px solid var(--colorBorderInput);border-radius:4px;padding:4px 8px;background:var(--colorBgInput);color:var(--colorTextMuted);font-family:monospace;width:200px"></div>`
          }
          <input id="cf-label" value="${sanitize(data.label || '')}" placeholder="Control Name" required
            style="font-size:18px;font-weight:500;color:var(--colorTextHeading);border:none;border-bottom:1px solid var(--colorBorderInput);background:transparent;width:100%;padding:4px 0;margin-bottom:8px;outline:none"
            onfocus="this.style.borderBottomColor='var(--colorAccent)'" onblur="this.style.borderBottomColor='var(--colorBorderInput)'">
          <input id="cf-desc" value="${sanitize(data.description || '')}" placeholder="Short description..."
            style="font-size:13px;color:var(--colorTextMuted);border:none;border-bottom:1px solid transparent;background:transparent;width:100%;padding:4px 0;outline:none"
            onfocus="this.style.borderBottomColor='var(--colorBorderInput)'" onblur="this.style.borderBottomColor='transparent'">
        </div>
      </div>

      <!-- Category (single-select tags with color dots) -->
      <div style="margin-bottom:18px">
        <div style="${labelStyle}">Category</div>
        <div id="cf-cat-tags" style="display:flex;flex-wrap:wrap;gap:6px">
          ${Object.entries(CTRL_CATEGORIES).map(([k, v]) => {
            const isActive = data.category === k;
            return `<span data-cat="${k}" data-cat-color="${v.color}" style="${chipBase};${isActive ? `background:${v.color};color:#fff;border-color:${v.color}` : chipInactive}">
              <span class="cat-dot" style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${isActive ? '#fff' : v.color};margin-right:6px;flex-shrink:0"></span>${v.label}
            </span>`;
          }).join('')}
        </div>
        <input type="hidden" id="cf-cat" value="${data.category || 'authentication'}">
      </div>

      <!-- Status (edit only) -->
      ${data._id ? `
      <div style="margin-bottom:20px">
        <div style="${labelStyle}">Status</div>
        <div id="cf-status-tags" style="display:flex;gap:6px">
          <span data-status-val="active" style="${chipBase};${currentStatus === 'active' ? 'background:var(--colorSuccess);color:#fff;border-color:var(--colorSuccess)' : chipInactive}">Active</span>
          <span data-status-val="hidden" style="${chipBase};${currentStatus === 'hidden' ? 'background:var(--colorWarning);color:#fff;border-color:var(--colorWarning)' : chipInactive}">Hidden</span>
        </div>
        <input type="hidden" id="cf-status" value="${currentStatus}">
        <div style="font-size:9px;color:var(--colorTextLight);margin-top:4px">Hidden controls won't appear in the control picker but are preserved in existing worksheets.</div>
      </div>
      ` : ''}

      <!-- Actions -->
      <div style="display:flex;gap:8px">
        <button type="button" onclick="this.closest('.modal-overlay').remove()" style="flex:1;padding:12px;border-radius:8px;font-size:14px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
        <button type="submit" style="flex:1;padding:12px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">${data._id ? 'Save' : 'Create'}</button>
      </div>
    </form>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Category tag selection (with category colors)
  document.querySelectorAll('#cf-cat-tags [data-cat]').forEach((el) => {
    el.addEventListener('click', () => {
      const catKey = (el as HTMLElement).dataset.cat!;
      const catColor = (el as HTMLElement).dataset.catColor!;
      // Reset all chips
      document.querySelectorAll('#cf-cat-tags [data-cat]').forEach((s) => {
        const sColor = (s as HTMLElement).dataset.catColor!;
        (s as HTMLElement).style.cssText = chipBase + ';' + chipInactive;
        const dot = s.querySelector('.cat-dot') as HTMLElement;
        if (dot) dot.style.background = sColor;
      });
      // Activate clicked chip
      (el as HTMLElement).style.cssText = chipBase + `;background:${catColor};color:#fff;border-color:${catColor}`;
      const dot = el.querySelector('.cat-dot') as HTMLElement;
      if (dot) dot.style.background = '#fff';
      (document.getElementById('cf-cat') as HTMLInputElement).value = catKey;
    });
  });

  // Status tag selection
  document.querySelectorAll('#cf-status-tags span').forEach((el) => {
    el.addEventListener('click', () => {
      const val = (el as HTMLElement).dataset.statusVal!;
      const colors: Record<string, string> = { active: 'background:var(--colorSuccess);color:#fff;border-color:var(--colorSuccess)', hidden: 'background:var(--colorWarning);color:#fff;border-color:var(--colorWarning)' };
      document.querySelectorAll('#cf-status-tags span').forEach((s) => {
        (s as HTMLElement).style.cssText = chipBase + ';' + chipInactive;
      });
      (el as HTMLElement).style.cssText = chipBase + ';' + (colors[val] || chipInactive);
      (document.getElementById('cf-status') as HTMLInputElement).value = val;
    });
  });

  // Form submit
  document.getElementById('control-modal-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    await onSubmit();
    overlay.remove();
    await loadControls();
    render();
  });

  setTimeout(() => (document.getElementById(data._id ? 'cf-label' : 'cf-id') as HTMLElement)?.focus(), 100);
}

function showCreateControlModal(): void {
  controlFormModal('New Control', {}, async () => {
    await controlCatalogService.create({
      control_id: (document.getElementById('cf-id') as HTMLInputElement).value.trim(),
      label: (document.getElementById('cf-label') as HTMLInputElement).value.trim(),
      category: (document.getElementById('cf-cat') as HTMLInputElement).value,
      description: (document.getElementById('cf-desc') as HTMLInputElement).value.trim(),
    });
  });
}

function showEditControlModal(control: any): void {
  controlFormModal('Edit Control', control, async () => {
    await controlCatalogService.update(control._id, {
      label: (document.getElementById('cf-label') as HTMLInputElement).value.trim(),
      description: (document.getElementById('cf-desc') as HTMLInputElement).value.trim(),
    });
    // Update status if changed
    const statusEl = document.getElementById('cf-status') as HTMLInputElement;
    if (statusEl && statusEl.value !== control.status) {
      await controlCatalogService.setStatus(control._id, statusEl.value as any);
    }
  });
}
